import { createFileRoute, useNavigate } from '@tanstack/react-router'
import {
  ChevronForward,
  ArrowDropDown,
  Sunny,
  MoonStars,
  Computer,
} from '@nine-thirty-five/material-symbols-react/rounded'
import {
  useLabelsStore,
  useGetUserCalendars,
  isWritingToExternal,
  useSaveUserSettings,
  useUserSettingsStore,
  DATE_FORMATS,
  WEEK_START_DAYS,
  type DateFormat,
  type TimeFormat,
  type WeekStart,
} from '@zendia/frontend-shared'
import { ActionBar } from '../../../components/ActionBar'
import { Menu, MenuButton, MenuItems, MenuItem } from '@headlessui/react'
import { useContext, useMemo, type ReactNode } from 'react'
import { ThemeContext, type Theme } from '../../../hooks/useTheme'
import { Themes, ThemeSources } from '../../../constants/theme.constants'
import { axiosRequest } from '../../../utilities/axiosClient'
import { useGoogleCalendarStatus } from '../../../hooks/useGoogleCalendar'
import classNames from 'classnames'

export const Route = createFileRoute('/_authenticated/settings/')({
  component: Settings,
})

const ThemeDisplayName = {
  [Themes.LIGHT]: 'Light',
  [Themes.DARK]: 'Dark',
  [ThemeSources.SYSTEM]: 'System Default',
}

const cardClassName =
  'flex flex-col mt-4 bg-white dark:bg-slate-900 rounded-xl border border-gray-200 dark:border-slate-800 py-2 px-3 sm:px-4'

/** The answers given during onboarding that the user can revisit, each on its own page. */
const PROFILE_SETTINGS = [
  { label: 'Life balance', to: '/settings/life-balance' },
  { label: 'Life satisfaction', to: '/settings/life-satisfaction' },
  { label: 'Your week', to: '/settings/week' },
  { label: 'Physical capacity', to: '/settings/physical-capacity' },
] as const

interface SettingsNavRowProps {
  label: string
  onClick: () => void
  /** Shown before the chevron, e.g. a count. */
  trailing?: ReactNode
  /** Draws the divider under every row but the last in its card. */
  divider?: boolean
}

/** A real button, so the row is reachable and operable from the keyboard. */
function SettingsNavRow({ label, onClick, trailing, divider = false }: SettingsNavRowProps) {
  return (
    <button
      type="button"
      className={classNames(
        'flex w-full items-center justify-between text-start cursor-pointer rounded-sm',
        'focus-visible:outline-2 focus-visible:outline-primary-500',
        { 'border-b border-gray-200 dark:border-slate-800': divider },
      )}
      onClick={onClick}
    >
      <span className="text-xl">{label}</span>
      <span className="py-2 flex items-center gap-2">
        {trailing}
        <ChevronForward className="size-8 inline-block" />
      </span>
    </button>
  )
}

const TIME_FORMAT_OPTIONS: { value: TimeFormat; label: string }[] = [
  { value: '12h', label: '12-hour' },
  { value: '24h', label: '24-hour' },
]

const DATE_FORMAT_OPTIONS = DATE_FORMATS.map((value) => ({ value, label: value }))

const WEEK_START_OPTIONS = WEEK_START_DAYS.map((value) => ({
  value,
  label: value.charAt(0).toUpperCase() + value.slice(1),
}))

/** IANA zones the browser knows, with the user's current one kept selectable if it is not listed. */
function getTimeZoneOptions(current: string | null | undefined): string[] {
  const supported =
    (Intl as unknown as { supportedValuesOf?: (key: string) => string[] }).supportedValuesOf?.(
      'timeZone',
    ) ?? []
  const zones = new Set(supported)
  zones.add('UTC')
  if (current) zones.add(current)
  return [...zones].sort((a, b) => a.localeCompare(b))
}

interface SettingsSelectRowProps {
  id: string
  label: string
  value: string
  options: { value: string; label: string }[]
  onChange: (value: string) => void
  divider?: boolean
}

/** A native select: the zone list is hundreds long, and native keeps it searchable and accessible. */
function SettingsSelectRow({
  id,
  label,
  value,
  options,
  onChange,
  divider = false,
}: SettingsSelectRowProps) {
  return (
    <div
      className={classNames('flex items-center justify-between gap-4 py-2', {
        'border-b border-gray-200 dark:border-slate-800': divider,
      })}
    >
      <label htmlFor={id} className="text-xl">
        {label}
      </label>
      <select
        id={id}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="max-w-[55%] rounded-lg border border-gray-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-2 py-1.5 text-lg text-dark dark:text-light"
      >
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </div>
  )
}

function Settings() {
  const navigate = useNavigate()
  const { labels } = useLabelsStore()
  const { theme, source, setTheme } = useContext(ThemeContext)
  const { mutate: saveUserSettings } = useSaveUserSettings(axiosRequest)
  const { settings } = useUserSettingsStore()
  const { data: googleCalendarStatus } = useGoogleCalendarStatus()

  const { data: zendiaCalendars } = useGetUserCalendars(
    axiosRequest,
    !!googleCalendarStatus?.connected,
  )

  // Calendars read from Google plus Zendia calendars written to it
  const enabledGoogleCalendars =
    (googleCalendarStatus?.calendars.filter((cal) => cal.enabled).length ?? 0) +
    (zendiaCalendars?.data?.filter((cal) => isWritingToExternal(cal)).length ?? 0)
  const googleCalendarSummary = !googleCalendarStatus
    ? null
    : googleCalendarStatus.connected
      ? `${enabledGoogleCalendars} synced`
      : 'Not connected'

  const onNavigateToLabels = () => {
    navigate({ to: '/settings/labels', from: '/settings/' })
  }

  const timeZone = settings?.defaultTimeZone ?? Intl.DateTimeFormat().resolvedOptions().timeZone
  const timeZoneOptions = useMemo(
    () => getTimeZoneOptions(timeZone).map((value) => ({ value, label: value })),
    [timeZone],
  )

  const onThemeSelect = (_theme?: Theme) => {
    // Applied locally straight away, with no server round trip in the way, and
    // persisted so it follows the user to their next browser/device too - see
    // routes/_authenticated.tsx, which applies themePreference back on load.
    setTheme(_theme)
    saveUserSettings({ themePreference: _theme ?? 'system' })
  }

  return (
    <>
      <ActionBar title="Settings" />
      <div className="flex flex-col p-4">
        <div className={cardClassName}>
          <SettingsNavRow
            label="Labels"
            onClick={onNavigateToLabels}
            trailing={<span className="text-xl">{labels.length}</span>}
            divider
          />
          <div className="flex items-center justify-between cursor-pointer" role="button">
            <div className="text-xl">Appearance</div>
            <div className="py-2 flex items-center -me-1">
              <Menu>
                <MenuButton className="inline-flex items-center rounded-xl gap-2 px-3 py-1.5 text-lg/6 font-semibold text-dark dark:text-light">
                  {theme === Themes.LIGHT && source === ThemeSources.USER && (
                    <Sunny className="size-6" />
                  )}
                  {theme === Themes.DARK && source === ThemeSources.USER && (
                    <MoonStars className="size-6" />
                  )}
                  {source === ThemeSources.SYSTEM && <Computer className="size-6" />}
                  <span className="uppercase">
                    {source === ThemeSources.SYSTEM
                      ? ThemeDisplayName[source]
                      : ThemeDisplayName[theme]}
                  </span>
                  <ArrowDropDown className="size-6" />
                </MenuButton>
                <MenuItems
                  transition
                  anchor="bottom end"
                  className={classNames(
                    'min-w-52 text-lg/6 p-2 border rounded-lg shadow-lg',
                    'bg-white border-gray-300/25',
                    'dark:bg-gray-800 dark:border-gray-600',
                  )}
                >
                  <MenuItem>
                    <button
                      className="group flex w-full items-center gap-2 rounded-lg px-3 py-1.5 data-focus:bg-gray-400/25"
                      onClick={() => onThemeSelect()}
                    >
                      <Computer className="size-6" />
                      {ThemeDisplayName[ThemeSources.SYSTEM]}
                    </button>
                  </MenuItem>
                  <MenuItem>
                    <button
                      className="group flex w-full items-center gap-2 rounded-lg px-3 py-1.5 data-focus:bg-gray-400/25"
                      onClick={() => onThemeSelect(Themes.LIGHT)}
                    >
                      <Sunny className="size-6" />
                      {ThemeDisplayName[Themes.LIGHT]}
                    </button>
                  </MenuItem>
                  <MenuItem>
                    <button
                      className="group flex w-full items-center gap-2 rounded-lg px-3 py-1.5 data-focus:bg-gray-400/25"
                      onClick={() => onThemeSelect(Themes.DARK)}
                    >
                      <MoonStars className="size-6" />
                      {ThemeDisplayName[Themes.DARK]}
                    </button>
                  </MenuItem>
                </MenuItems>
              </Menu>
            </div>
          </div>
        </div>

        <h2 className="text-lg font-semibold mt-8 px-1">Region</h2>
        <p className="text-sm text-neutral-500 px-1">How times, dates and weeks are shown.</p>
        <div className={cardClassName}>
          <SettingsSelectRow
            id="setting-time-zone"
            label="Time zone"
            value={timeZone}
            options={timeZoneOptions}
            onChange={(defaultTimeZone) => saveUserSettings({ defaultTimeZone })}
            divider
          />
          <SettingsSelectRow
            id="setting-time-format"
            label="Time format"
            value={settings?.timeFormat ?? '12h'}
            options={TIME_FORMAT_OPTIONS}
            onChange={(timeFormat) => saveUserSettings({ timeFormat: timeFormat as TimeFormat })}
            divider
          />
          <SettingsSelectRow
            id="setting-date-format"
            label="Date format"
            value={settings?.dateFormat ?? 'MM/DD/YYYY'}
            options={DATE_FORMAT_OPTIONS}
            onChange={(dateFormat) => saveUserSettings({ dateFormat: dateFormat as DateFormat })}
            divider
          />
          <SettingsSelectRow
            id="setting-week-start"
            label="Week starts on"
            value={settings?.weekStart ?? 'sunday'}
            options={WEEK_START_OPTIONS}
            onChange={(weekStart) => saveUserSettings({ weekStart: weekStart as WeekStart })}
          />
        </div>

        <h2 className="text-lg font-semibold mt-8 px-1">Calendar</h2>
        <p className="text-sm text-neutral-500 px-1">
          Bring the appointments you already have into Zendia.
        </p>
        <div className={cardClassName}>
          <SettingsNavRow
            label="Google Calendar"
            onClick={() => navigate({ to: '/settings/calendar' })}
            trailing={
              googleCalendarSummary && (
                <span className="text-base text-neutral-500">{googleCalendarSummary}</span>
              )
            }
          />
        </div>

        <h2 className="text-lg font-semibold mt-8 px-1">About you</h2>
        <p className="text-sm text-neutral-500 px-1">
          The answers you gave when you joined. Change them whenever your life does.
        </p>
        <div className={cardClassName}>
          {PROFILE_SETTINGS.map(({ label, to }, index) => (
            <SettingsNavRow
              key={to}
              label={label}
              onClick={() => navigate({ to })}
              divider={index < PROFILE_SETTINGS.length - 1}
            />
          ))}
        </div>
      </div>
    </>
  )
}
